import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiagramProject, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';

// Mêmes rôles que le groupe de routes /projects côté serveur, moins charge_etude qui a son
// propre tableau de bord. Un rôle absent d'ici ne voit rien — c'est le cas de chef_terrain,
// qui partage l'écran de son supérieur sans avoir accès aux projets.
const ALLOWED_ROLES = [
  'administrateur_general',
  'administrateur_general_superieur',
  'chef_terrain_superieur',
  'charge_etude_superieur',
  'resp_controle',
  'resp_admin',
];

const STEPS = [
  { key: 'ao', label: 'Avant-vente', color: '#991b1b' },
  { key: 'setup', label: 'Setup', color: '#d97706' },
  { key: 'terrain', label: 'Terrain', color: '#059669' },
  { key: 'reporting', label: 'Reporting', color: '#0284c7' },
  { key: 'closing', label: 'Closing', color: '#7c3aed' },
];

const CLOSING_STEPS = [
  { key: 'livrables', label: 'Livrables', color: '#d97706' },
  { key: 'reception', label: 'Réception', color: '#0891b2' },
  { key: 'facturation', label: 'Facturation', color: '#7c3aed' },
  { key: 'attente_paiement', label: 'En attente de paiement', color: '#ea580c' },
  { key: 'encaisse', label: 'Encaissé', color: '#059669' },
];

const STORAGE_KEY = 'dashboard_projects_closing_open';

const readStoredOpen = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Navigation privée ou stockage bloqué : on retombe sur « replié ».
    return false;
  }
};

/**
 * Compteurs des projets pour le tableau de bord.
 *
 * Le total vit dans l'en-tête, pas dans la rangée : posé à côté des étapes, il se lisait
 * comme une sixième étape. Les cinq étapes forment un seul bloc avec une barre proportionnelle,
 * pour qu'on lise un pipeline plutôt que cinq indicateurs sans rapport.
 *
 * Le détail du closing compte les éléments du board (enquêtes en closing + projets sans
 * enquête visible), pas les projets à l'étape closing : un projet peut avoir ses enquêtes
 * réparties sur plusieurs colonnes, le total ne coïncide donc pas avec la cellule « Closing ».
 */
const ProjectsStatsCards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closingOpen, setClosingOpen] = useState(readStoredOpen);

  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);

  const allowed = ALLOWED_ROLES.includes(user?.role);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    let cancelled = false;
    api.get('/projects/dashboard-stats')
      .then(res => { if (!cancelled) setStats(res.data.data || null); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [allowed]);

  // Hauteur mesurée plutôt qu'un max-height arbitraire : la transition reste régulière même
  // quand le panneau passe sur plusieurs lignes en écran étroit.
  useLayoutEffect(() => {
    if (!panelRef.current) return undefined;
    const measure = () => setPanelHeight(panelRef.current ? panelRef.current.scrollHeight : 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stats, closingOpen]);

  const toggleClosing = () => {
    setClosingOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* stockage indisponible */ }
      return next;
    });
  };

  if (!allowed) return null;

  // Une fois le chargement terminé : rien à afficher si l'utilisateur n'a aucun projet visible.
  if (!loading && (!stats || !stats.total)) return null;

  // Pendant le chargement, la structure s'affiche avec des 0 — comme les cartes
  // CATI/F2F/Contrôle — plutôt qu'un spinner qui remplace tout le bloc.
  const data = stats || {};
  const closing = data.closing || { total: 0, by_step: {} };
  const closingSurveys = closing.surveys || { total: 0, by_step: {} };
  const surveys = data.surveys || { total: 0, by_step: {} };
  const total = data.total || 0;
  const surveysTotal = surveys.total || 0;

  return (
    <div className="mb-4 sm:mb-6">
      {/* En-tête : le total est ici, isolé des étapes, pour se lire comme la somme */}
      <div className="flex items-end justify-between gap-3 mb-2 sm:mb-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <FontAwesomeIcon icon={faDiagramProject} className="text-gray-400 text-xs sm:text-sm flex-shrink-0" />
          <h2 className="text-sm sm:text-base font-bold text-gray-900 flex-shrink-0">Projets</h2>
          <span className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight leading-none">{total}</span>
            <span className="text-[10px] sm:text-xs text-gray-500">
              {total > 1 ? 'projets' : 'projet'}
            </span>
            <span className="text-gray-300 text-[10px] sm:text-xs">·</span>
            <span className="text-base sm:text-xl font-bold text-gray-600 tracking-tight leading-none">{surveysTotal}</span>
            <span className="text-[10px] sm:text-xs text-gray-500 truncate">
              {surveysTotal > 1 ? 'enquêtes' : 'enquête'}
            </span>
          </span>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="text-[10px] sm:text-xs font-semibold text-red-600 hover:text-red-700 flex-shrink-0"
        >
          Voir le kanban
        </button>
      </div>

      {/* Le pipeline : une seule carte, barre proportionnelle sur l'arête haute */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Sous 640px : une étape par ligne. Cinq colonnes sur un écran de 370px faisaient
            passer « AVANT-VENTE » sur deux lignes et collaient les libellés aux séparateurs. */}
        <div className="grid grid-cols-1 sm:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
          {STEPS.map(step => {
            const count = data.by_step?.[step.key] ?? 0;
            const surveyCount = surveys.by_step?.[step.key] ?? 0;
            const isClosing = step.key === 'closing';
            const cellTitle = `${count} projet${count > 1 ? 's' : ''} · ${surveyCount} enquête${surveyCount > 1 ? 's' : ''}`;

            const cell = (
              // Ligne (libellé / chiffres) en mobile, bloc empilé dès 640px.
              <div className="flex items-center justify-between gap-2 sm:block">
                <div className="flex items-center gap-1.5 min-w-0 sm:mb-2">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: step.color }} />
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide leading-tight">
                    {step.label}
                  </span>
                  {isClosing && (
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-[10px] text-gray-400 flex-shrink-0 sm:ml-auto transition-transform duration-200 ${closingOpen ? 'rotate-180' : ''}`}
                    />
                  )}
                </div>
                <div className="flex items-baseline gap-1 flex-shrink-0">
                  <span className={`text-lg sm:text-2xl font-bold tracking-tight ${count ? 'text-gray-900' : 'text-gray-300'}`}>
                    {count}
                  </span>
                  {/* Le mot tient en mobile (une ligne entière par étape) et à partir de md ;
                      entre les deux, cinq colonnes étroites ne lui laissent pas la place. */}
                  <span className="text-[10px] sm:text-xs text-gray-400 whitespace-nowrap">
                    ({surveyCount}<span className="inline sm:hidden md:inline"> enquête{surveyCount > 1 ? 's' : ''}</span>)
                  </span>
                </div>
              </div>
            );

            if (!isClosing) {
              return (
                <div key={step.key} className="p-2.5 sm:p-4" title={cellTitle}>{cell}</div>
              );
            }

            return (
              <button
                key={step.key}
                type="button"
                onClick={toggleClosing}
                aria-expanded={closingOpen}
                aria-controls="projects-closing-detail"
                title={`${cellTitle} — cliquer pour le détail`}
                className="p-2.5 sm:p-4 text-left w-full hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors cursor-pointer"
              >
                {cell}
              </button>
            );
          })}
        </div>

        {/* Détail du closing, dans la même carte */}
        <div
          id="projects-closing-detail"
          style={{ maxHeight: closingOpen ? panelHeight : 0 }}
          className="overflow-hidden transition-all duration-200 ease-in-out"
          aria-hidden={!closingOpen}
        >
          <div ref={panelRef} className="border-t border-gray-200 bg-gray-50 px-2.5 sm:px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] sm:text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                Détail du closing
              </span>
              <span className="text-[9px] sm:text-[10px] text-gray-400">
                {closing.total} projet{closing.total > 1 ? 's' : ''}
                {' · '}
                {closingSurveys.total} enquête{closingSurveys.total > 1 ? 's' : ''}
              </span>
            </div>
            {/* Même logique que la rangée des étapes : une sous-étape par ligne en mobile. */}
            <div className="grid grid-cols-1 sm:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
              {CLOSING_STEPS.map(sub => {
                const count = closing.by_step?.[sub.key] ?? 0;
                const subSurveys = closingSurveys.by_step?.[sub.key] ?? 0;
                return (
                  <div key={sub.key}
                    className="py-2 first:pt-0 last:pb-0 sm:py-0 sm:px-3 sm:first:pl-0 sm:last:pr-0"
                    title={`${count} projet${count > 1 ? 's' : ''} · ${subSurveys} enquête${subSurveys > 1 ? 's' : ''}`}>
                    <div className="flex items-center justify-between gap-2 sm:block">
                      <div className="flex items-center gap-1.5 min-w-0 sm:mb-1">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sub.color }} />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide leading-tight">
                          {sub.label}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1 flex-shrink-0">
                        <span className={`text-sm sm:text-lg font-bold tracking-tight ${count ? 'text-gray-900' : 'text-gray-300'}`}>
                          {count}
                        </span>
                        <span className="text-[9px] sm:text-[10px] text-gray-400 whitespace-nowrap">
                          ({subSurveys}<span className="inline sm:hidden lg:inline"> enquête{subSurveys > 1 ? 's' : ''}</span>)
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Sans cette note, la somme des étapes (supérieure au total) passe pour une erreur. */}
      <p className="mt-1.5 text-[10px] sm:text-[11px] text-gray-400">
        Un projet apparaît dans chaque étape où il a une enquête active : la somme des étapes
        peut donc dépasser le total.
      </p>
    </div>
  );
};

export default ProjectsStatsCards;
